/**
 * GW-9568: PII-URL scrubbing for Sentry events.
 *
 * Sentry stays always-on (legitimate interest) with `sendDefaultPii: false`, but
 * the URLs it captures on error events, breadcrumbs, the referrer header, and
 * performance transactions (pageload trace URL + span URLs/queries/descriptions)
 * can still carry personal data in query params — e.g. `?q=<search terms>` on the
 * marketplace search or `?email=<address>` on the password-reset flow. This
 * module redacts a narrow denylist of param VALUES while preserving everything
 * else (path, utm_*, nav, facets) so error attribution and grouping are
 * unaffected.
 *
 * The logic mirrors the GW-9567 GTM-console URL/referrer scrubbing: same
 * denylist, same fail-closed behavior, same same-origin referrer guard. This is
 * the first codebase home for the denylist — extend the const below rather than
 * forking the list elsewhere.
 *
 * GW-9673: in addition to the static denylist, callers can opt a URL into
 * blanket redaction by adding a `sensitive=t` query-string marker. When that
 * marker is present, the VALUES of every other param are redacted (the marker
 * itself is kept verbatim so the signal stays visible in Sentry). This lets
 * feature code flag a URL it knows carries PII in arbitrary params without
 * enumerating every param name in the denylist here.
 *
 * GW-9674: align this module byte-for-byte with the FINAL GW-9567 GTM-console
 * sanitizer (the source of truth) so both redact identically on the same URL:
 *   1. ALWAYS_SAFE allowlist — attribution/nav params (utm_*, click IDs, ref)
 *      are preserved even under blanket (sensitive) mode, matching GTM, so
 *      campaign attribution is never destroyed.
 *   2. The blanket marker triggers on `sensitive=t` OR `sensitive=true`
 *      (compared lowercased) — GW-9653 may emit `sensitive=true`; missing it
 *      would leak phone/name/location into Sentry.
 *   3. Case-insensitive matching — GTM lowercases the flag name, flag value,
 *      and denylist/ALWAYS_SAFE names before comparing (querystrings are
 *      case-sensitive by spec, so this deliberately over-captures rather than
 *      miss PII on a stray-case param like `?Q=` or `?Sensitive=T`). The param
 *      is still redacted by its REAL (original-case) name.
 *   4. The marker is detected by scanning ALL keys (not just the first
 *      `sensitive` value), matching GTM's `keys.some(...)`.
 */

/**
 * Query params whose VALUES are redacted (keys are preserved). Kept as a const
 * so future PII-bearing params can be added in one place. Matched
 * case-insensitively (GW-9674) so `?Q=`/`?EMAIL=` are caught too.
 */
export const PII_QUERY_PARAM_DENYLIST = ['q', 'email'] as const;

/**
 * GW-9674: attribution/navigation params that are ALWAYS preserved verbatim,
 * even under blanket (sensitive) mode. Mirrors the GW-9567 GTM sanitizer
 * exactly. Applies ONLY in blanket mode — the normal-mode denylist floor
 * (`PII_QUERY_PARAM_DENYLIST`) is unaffected. Matched case-insensitively.
 */
export const ALWAYS_SAFE_QUERY_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'ref',
  'referrer',
] as const;

const REDACTED = '[redacted]';

/**
 * GW-9673/GW-9674: opt-in blanket-redaction marker. When a URL carries
 * `sensitive=t` or `sensitive=true` (flag name and value compared lowercased),
 * every non-marker, non-ALWAYS_SAFE param value is redacted (not just the
 * denylist). Any other value (e.g. `sensitive=x`, empty) is ignored and the
 * denylist-only path applies.
 */
const SENSITIVE_MARKER_KEY = 'sensitive';
const SENSITIVE_MARKER_VALUES = new Set(['t', 'true']);

const DENYLIST_LOWER = new Set<string>(PII_QUERY_PARAM_DENYLIST.map((k) => k.toLowerCase()));
const ALWAYS_SAFE_LOWER = new Set<string>(ALWAYS_SAFE_QUERY_PARAMS.map((k) => k.toLowerCase()));

/**
 * GW-9674: detect the blanket marker by scanning ALL keys case-insensitively
 * (GTM uses `keys.some(...)`), not just the first `sensitive` value. Returns
 * true when any key lowercases to `sensitive` and its value lowercases to `t`
 * or `true`.
 */
function hasSensitiveMarker(params: URLSearchParams): boolean {
  for (const [key, value] of params.entries()) {
    if (
      key.toLowerCase() === SENSITIVE_MARKER_KEY &&
      SENSITIVE_MARKER_VALUES.has(value.toLowerCase())
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Redact PII param values on a mutable URLSearchParams in place. Keys are always
 * preserved.
 *
 * - If the `sensitive=t`/`sensitive=true` marker (GW-9673/GW-9674, matched
 *   case-insensitively across ALL keys) is present, redact the VALUE of EVERY
 *   param except (a) the marker key itself and (b) any ALWAYS_SAFE attribution/
 *   nav param. The marker and ALWAYS_SAFE params are left verbatim so the "this
 *   URL was flagged sensitive" signal and campaign attribution stay visible in
 *   Sentry, matching the GW-9567 GTM sanitizer.
 * - Otherwise, redact only the denylisted param values (GW-9568), matched
 *   case-insensitively; all other params are left untouched.
 *
 * In both modes, membership tests are case-insensitive but redaction always
 * targets the param's REAL (original-case) name, so `?Q=foo` -> `Q=[redacted]`
 * and a mixed-case duplicate `?Q=a&q=b` redacts BOTH.
 */
function redactParams(params: URLSearchParams): void {
  if (hasSensitiveMarker(params)) {
    // Blanket redaction: iterate a de-duplicated real-key set so repeated keys
    // are handled once. Skip the marker key and any ALWAYS_SAFE param
    // (both compared case-insensitively) so they stay verbatim.
    for (const key of new Set(params.keys())) {
      const lower = key.toLowerCase();
      if (lower !== SENSITIVE_MARKER_KEY && !ALWAYS_SAFE_LOWER.has(lower)) {
        params.set(key, REDACTED);
      }
    }
    return;
  }

  // Normal mode: denylist floor, matched case-insensitively but redacted by
  // the param's real-case key.
  for (const key of new Set(params.keys())) {
    if (DENYLIST_LOWER.has(key.toLowerCase())) {
      params.set(key, REDACTED);
    }
  }
}

/**
 * Strip everything after the first `?` or `#` from a raw string. Used as the
 * fail-closed fallback when a string cannot be parsed as a URL, so no raw query
 * or fragment leaks into Sentry.
 */
function stripQueryAndHash(raw: string): string {
  const queryIndex = raw.indexOf('?');
  const hashIndex = raw.indexOf('#');
  let cut = raw.length;
  if (queryIndex !== -1) cut = Math.min(cut, queryIndex);
  if (hashIndex !== -1) cut = Math.min(cut, hashIndex);
  return raw.slice(0, cut);
}

/**
 * Read the current browser origin, guarding non-browser/test environments.
 * Returns `undefined` when there is no `window.location` (SSR/node).
 */
function currentOrigin(): string | undefined {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') {
    return undefined;
  }
  return window.location.origin;
}

/**
 * Scrub PII from a URL string: redact denylisted param values (keeping keys and
 * all other params + the path), and drop the hash fragment.
 *
 * Fails closed on parse failure: returns `origin + pathname` if derivable, else
 * the input stripped of everything after `?`/`#`. Never throws, never leaks the
 * raw query.
 */
export function scrubUrl(rawUrl: string): string {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    // Relative URLs and other non-absolute inputs land here. Resolve against a
    // placeholder base so we can still redact params, then re-derive a relative
    // string. If even that fails, strip query + hash as a last resort.
    try {
      const base = new URL(rawUrl, 'http://placeholder.invalid');
      redactParams(base.searchParams);
      base.hash = '';
      const query = base.search;
      return `${base.pathname}${query}`;
    } catch {
      return stripQueryAndHash(rawUrl);
    }
  }
  redactParams(url.searchParams);
  url.hash = '';
  return url.toString();
}

/**
 * Read the current page hostname (`window.location.hostname`), guarding
 * non-browser/test contexts. Returns `undefined` when there is no
 * `window.location` (SSR/node).
 */
function currentHostname(): string | undefined {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') {
    return undefined;
  }
  return window.location.hostname;
}

/**
 * GW-9675: true when `hostname` (a parsed `URL.hostname`) is INTERNAL — i.e. it
 * is the page's own hostname or a subdomain of it. The base is the LIVE page
 * hostname (`window.location.hostname`), not a hardcoded domain: served at
 * `dev.generalwisdom.com`, both `dev.generalwisdom.com` and every
 * `*.dev.generalwisdom.com` (e.g. `tokens.dev.generalwisdom.com` API calls) are
 * internal, while the apex `generalwisdom.com`, `platform.generalwisdom.com`,
 * `google-analytics.com`, and a look-alike `dev.generalwisdom.com.evil.com` are
 * all external.
 *
 * The suffix check is dot-anchored (`.${pageHost}`) so `xdev.generalwisdom.com`
 * does NOT count as a subdomain of `dev.generalwisdom.com`. When there is no
 * page hostname (non-browser), every absolute host is treated as external — the
 * safe, value-redacting direction.
 */
export function isInternalHostname(hostname: string): boolean {
  const page = currentHostname();
  if (page === undefined) return false;
  const host = hostname.toLowerCase();
  const pageHost = page.toLowerCase();
  return host === pageHost || host.endsWith(`.${pageHost}`);
}

/**
 * Redact the VALUE of every param on a mutable URLSearchParams in place, keeping
 * every key. Unlike `redactParams`, this applies NO denylist and NO ALWAYS_SAFE
 * allowlist — it is used only for EXTERNAL URLs (GW-9675), where we don't trust
 * the vendor's param semantics, so nothing is preserved except the keys (which
 * reveal the request's shape without leaking values).
 */
function redactAllParamValues(params: URLSearchParams): void {
  for (const key of new Set(params.keys())) {
    params.set(key, REDACTED);
  }
}

/**
 * GW-9675: redact the VALUES of a bare external query fragment (e.g. a span's
 * `http.query`), keeping keys. Fails closed: on a fragment that cannot be
 * parsed, returns an empty string rather than leaking it.
 */
export function scrubExternalQuery(rawQuery: string): string {
  try {
    const params = new URLSearchParams(rawQuery.replace(/^\?/, ''));
    redactAllParamValues(params);
    return params.toString();
  } catch {
    return '';
  }
}

/**
 * GW-9675: scrub a URL that may point at a THIRD-PARTY host. Sentry's
 * browser-tracing integration auto-captures every outgoing fetch/XHR as a span
 * and breadcrumb, including third-party analytics/vendor beacons (Google
 * Analytics `/g/collect`, Stripe, Checkr, …). Those URLs carry PII in params we
 * don't control and can't enumerate — e.g. GA4's `ep.search_term=<raw q>`, which
 * the `q`/`email` denylist does not match.
 *
 * Rule (host-based, not param-name-based, so it generalizes to any vendor):
 * - Internal URL (the page's own hostname or a subdomain of it — see
 *   `isInternalHostname` — or a host-less relative URL) -> delegate to `scrubUrl`
 *   (q/email denylist + `sensitive=t` marker; non-PII params preserved for
 *   useful tracing).
 * - External URL -> redact the VALUE of EVERY query param (keys kept so the
 *   beacon's shape stays debuggable) and drop the hash (unknown structure). No
 *   denylist and no `sensitive` handling apply. Because it's value-level, no
 *   vendor param — known (`ep.search_term`) or future — can leak.
 *
 * Fails closed exactly like `scrubUrl`: never throws, never leaks a raw value.
 */
export function scrubExternalUrl(rawUrl: string): string {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    // Non-absolute input (relative path or garbage). A relative URL is
    // host-less and therefore same-origin/internal — defer to scrubUrl, which
    // already handles the relative + fail-closed cases.
    return scrubUrl(rawUrl);
  }
  if (isInternalHostname(url.hostname)) {
    return scrubUrl(rawUrl);
  }
  // External: redact every param VALUE (keep keys), drop the hash.
  redactAllParamValues(url.searchParams);
  url.hash = '';
  return url.toString();
}

/**
 * Scrub PII from a referrer string.
 *
 * - Empty/falsy referrer -> `undefined` (let Sentry apply its default).
 * - Cross-origin referrer -> returned UNTOUCHED, preserving attribution.
 * - Same-origin referrer -> denylist redaction + hash drop (as `scrubUrl`).
 * - Unparseable referrer -> returned as-is (pass through rather than guess),
 *   matching the GW-9567 GTM referrer-variable behavior.
 */
export function scrubReferrer(rawRef: string): string | undefined {
  if (!rawRef) return undefined;

  let ref: URL;
  try {
    ref = new URL(rawRef);
  } catch {
    return rawRef;
  }

  const origin = currentOrigin();
  // Same-origin guard: only scrub when the referrer shares the current origin.
  // Cross-origin referrers are preserved untouched for attribution. When the
  // origin is undefined (non-browser), treat as not-same-origin and pass through.
  if (origin === undefined || ref.origin !== origin) {
    return rawRef;
  }

  redactParams(ref.searchParams);
  ref.hash = '';
  return ref.toString();
}
